import * as React from 'react';
import Banner from '@joinforma/ui/components/Banner';
import { BannerVariant } from '@joinforma/ui/constants/properties';

import {
  ALEGEUS_GROUP_ONE_ACCESS_DATE,
  ALEGEUS_GROUP_TWO_ACCESS_DATE,
  ALEGEUS_HSA_INVESTMENTS_SELL_DEADLINE,
  ALEGEUS_INDIVIDUAL_TRANSFER_REMINDER_LAST_DATE,
  SHOW_ALEGEUS_HSA_RETAIL_ELEMENTS_DATE,
  HIDE_ALEGEUS_HSA_RETAIL_ELEMENTS_DATE,
  MemberHsaTransferType,
} from 'constants/hsaBulkTransfer';
import { PretaxHsaBulkTransfer } from 'contexts/PretaxContext/types';
import {
  AlegeusSmallStoreBanner217V1,
  AlegeusSmallStoreBanner217V2,
  AlegeusLargeAccountsListOldHsaNewHsa217Banner,
  AlegeusLargeAccountsListOldHsaNewHsa217V2Banner,
  AlegeusCallout217V1,
  NonAlegeusSmallStoreTransferBanner,
  NonAlegeusSmallStoreConfirmBanner,
  NonAlegeusLargeAccountsListTransferBanner,
  NonAlegeusLargeNewHsaTransferBanner,
  NonAlegeusSmallAccountsListNewHsaMissedBanner,
  AlegeusLargeAccountsListRetailBanner,
  AlegeusCalloutRetail,
} from 'components/common/HsaBulkTransferComponents';
import dayjs from 'utils/date-helper';

export const displayHsaBulkTransferBannersOnStore = (
  hsaBulkTransfer?: PretaxHsaBulkTransfer
) => {
  // if hsaBulkTransfer is falsy, it means hsa bulk transfer does not apply to the user
  if (
    !hsaBulkTransfer ||
    hsaBulkTransfer?.memberStatus === MemberHsaTransferType.Hide
  ) {
    return null;
  }
  const isTodayPriorToOptInWindow = dayjs().isBefore(
    hsaBulkTransfer.optInStartDate
  );

  if (isTodayPriorToOptInWindow) {
    return null;
  }

  const isAlegeusCustodian = hsaBulkTransfer.custodianName === 'alegeus';
  const isNonAlegeusCustodian = !isAlegeusCustodian;
  const memberAcceptedTerms =
    hsaBulkTransfer.memberStatus === MemberHsaTransferType.Accept;
  const isWithinOptInWindow = dayjs().isBetween(
    dayjs(hsaBulkTransfer.optInStartDate),
    dayjs(hsaBulkTransfer.transferDeadline),
    'day',
    '[]'
  );
  const isTodayBeforeInvestmentsSellDeadline = dayjs().isSameOrBefore(
    ALEGEUS_HSA_INVESTMENTS_SELL_DEADLINE
  );
  const isTodayBeforeFundsTransferDatePlusSevenDays = dayjs().isSameOrBefore(
    dayjs(hsaBulkTransfer.fundsTransferDate).add(7, 'day').toDate()
  );
  const isTodayBeforeIndividualTransferReminderExpirationDate =
    dayjs().isSameOrBefore(ALEGEUS_INDIVIDUAL_TRANSFER_REMINDER_LAST_DATE);
  const memberSoldInvestments =
    !hsaBulkTransfer.memberHasAlegeusHsaInvestmentBalance;
  const memberCompletedRequirements =
    memberAcceptedTerms && memberSoldInvestments;

  if (
    isAlegeusCustodian &&
    memberCompletedRequirements &&
    isTodayBeforeFundsTransferDatePlusSevenDays
  ) {
    return (
      <Banner
        variant={BannerVariant.WARNING}
        content={
          <AlegeusSmallStoreBanner217V1
            isMemberAlegeusGroupTwo={hsaBulkTransfer.isMemberAlegeusGroupTwo}
          />
        }
      />
    );
  }

  if (
    isAlegeusCustodian &&
    !memberCompletedRequirements &&
    !isWithinOptInWindow &&
    !isTodayBeforeInvestmentsSellDeadline &&
    isTodayBeforeIndividualTransferReminderExpirationDate
  ) {
    return (
      <Banner
        variant={BannerVariant.WARNING}
        content={<AlegeusSmallStoreBanner217V2 />}
      />
    );
  }

  if (
    isNonAlegeusCustodian &&
    memberAcceptedTerms &&
    isTodayBeforeFundsTransferDatePlusSevenDays
  ) {
    return (
      <Banner
        variant={BannerVariant.WARNING}
        content={
          <NonAlegeusSmallStoreConfirmBanner
            fundsTransferDate={hsaBulkTransfer.fundsTransferDate}
          />
        }
      />
    );
  }

  if (isNonAlegeusCustodian && !memberAcceptedTerms && isWithinOptInWindow) {
    return (
      <Banner
        variant={BannerVariant.WARNING}
        content={
          <NonAlegeusSmallStoreTransferBanner
            onClick={hsaBulkTransfer.openModal}
          />
        }
      />
    );
  }

  return null;
};

export const displayHsaBulkTransferBannersOnAccountsListPage = (
  hsaBulkTransfer?: PretaxHsaBulkTransfer
) => {
  // if hsaBulkTransfer is falsy, it means hsa bulk transfer does not apply to the user
  if (
    !hsaBulkTransfer ||
    hsaBulkTransfer?.memberStatus === MemberHsaTransferType.Hide
  ) {
    return null;
  }

  const isTodayPriorToOptInWindow = dayjs().isBefore(
    hsaBulkTransfer.optInStartDate
  );

  if (isTodayPriorToOptInWindow) {
    return null;
  }

  const isAlegeusCustodian = hsaBulkTransfer.custodianName === 'alegeus';
  const isNonAlegeusCustodian = !isAlegeusCustodian;
  const memberAcceptedTerms =
    hsaBulkTransfer.memberStatus === MemberHsaTransferType.Accept;
  const isWithinOptInWindow = dayjs().isBetween(
    dayjs(hsaBulkTransfer.optInStartDate),
    dayjs(hsaBulkTransfer.transferDeadline),
    'day',
    '[]'
  );
  const isTodayBeforeFundsTransferDatePlusSevenDays = dayjs().isSameOrBefore(
    dayjs(hsaBulkTransfer.fundsTransferDate).add(7, 'day')
  );
  const isTodayBeforeIndividualTransferReminderExpirationDate =
    dayjs().isSameOrBefore(ALEGEUS_INDIVIDUAL_TRANSFER_REMINDER_LAST_DATE);

  const memberSoldInvestments =
    !hsaBulkTransfer.memberHasAlegeusHsaInvestmentBalance;

  const memberCompletedRequirements =
    memberAcceptedTerms && memberSoldInvestments;

  const shouldShowAlegeusHsaRetailElements = dayjs().isBetween(
    SHOW_ALEGEUS_HSA_RETAIL_ELEMENTS_DATE,
    HIDE_ALEGEUS_HSA_RETAIL_ELEMENTS_DATE,
    'day',
    '[]'
  );

  if (
    isAlegeusCustodian &&
    memberCompletedRequirements &&
    isTodayBeforeFundsTransferDatePlusSevenDays
  ) {
    return (
      <AlegeusLargeAccountsListOldHsaNewHsa217Banner
        isMemberAlegeusGroupTwo={hsaBulkTransfer.isMemberAlegeusGroupTwo}
        alegeusAccountId={hsaBulkTransfer.alegeusHsaAccountId}
      />
    );
  }

  if (
    isAlegeusCustodian &&
    !memberCompletedRequirements &&
    isTodayBeforeIndividualTransferReminderExpirationDate
  ) {
    return <AlegeusLargeAccountsListOldHsaNewHsa217V2Banner />;
  }

  if (
    isAlegeusCustodian &&
    hsaBulkTransfer.memberHasAlegeusRetailHsa &&
    !memberAcceptedTerms &&
    shouldShowAlegeusHsaRetailElements
  ) {
    return <AlegeusLargeAccountsListRetailBanner />;
  }

  if (
    isNonAlegeusCustodian &&
    memberAcceptedTerms &&
    isTodayBeforeFundsTransferDatePlusSevenDays
  ) {
    return (
      <Banner
        variant={BannerVariant.WARNING}
        content={
          <NonAlegeusSmallStoreConfirmBanner
            fundsTransferDate={hsaBulkTransfer.fundsTransferDate}
          />
        }
      />
    );
  }

  if (isNonAlegeusCustodian && !memberAcceptedTerms && isWithinOptInWindow) {
    return (
      <NonAlegeusLargeAccountsListTransferBanner
        optInDeadline={hsaBulkTransfer.transferDeadline}
        onClick={hsaBulkTransfer.openModal}
      />
    );
  }

  if (
    isNonAlegeusCustodian &&
    !memberAcceptedTerms &&
    !isWithinOptInWindow &&
    isTodayBeforeFundsTransferDatePlusSevenDays
  ) {
    return (
      <Banner
        variant={BannerVariant.WARNING}
        content={<NonAlegeusSmallAccountsListNewHsaMissedBanner />}
      />
    );
  }

  return null;
};

export const displayHsaBulkTransferBannersOnAlegeusHsaDetailPage = (
  hsaBulkTransfer?: PretaxHsaBulkTransfer
) => {
  // if hsaBulkTransfer is falsy, it means hsa bulk transfer does not apply to the user
  if (
    !hsaBulkTransfer ||
    hsaBulkTransfer?.memberStatus === MemberHsaTransferType.Hide
  ) {
    return null;
  }
  const isTodayPriorToOptInWindow = dayjs().isBefore(
    hsaBulkTransfer.optInStartDate
  );

  if (isTodayPriorToOptInWindow) {
    return null;
  }

  const isWithinOptInWindow = dayjs().isBetween(
    dayjs(hsaBulkTransfer.optInStartDate),
    dayjs(hsaBulkTransfer.transferDeadline),
    'day',
    '[]'
  );

  const memberAcceptedTerms =
    hsaBulkTransfer.memberStatus === MemberHsaTransferType.Accept;

  const isTodayBeforeInvestmentsSellDeadline = dayjs().isSameOrBefore(
    ALEGEUS_HSA_INVESTMENTS_SELL_DEADLINE
  );
  const isTodayBeforeFundsTransferDatePlusSevenDays = dayjs().isSameOrBefore(
    dayjs(hsaBulkTransfer.fundsTransferDate).add(7, 'day')
  );
  const isTodayBeforeIndividualTransferReminderExpirationDate =
    dayjs().isSameOrBefore(ALEGEUS_INDIVIDUAL_TRANSFER_REMINDER_LAST_DATE);
  const memberSoldInvestments =
    !hsaBulkTransfer.memberHasAlegeusHsaInvestmentBalance;
  const memberIsGroupOne = !hsaBulkTransfer.isMemberAlegeusGroupTwo;
  const memberCompletedRequirements =
    memberAcceptedTerms && memberSoldInvestments;

  if (
    memberAcceptedTerms &&
    memberCompletedRequirements &&
    memberIsGroupOne &&
    isTodayBeforeFundsTransferDatePlusSevenDays
  ) {
    return (
      <AlegeusLargeAccountsListOldHsaNewHsa217Banner
        isMemberAlegeusGroupTwo={hsaBulkTransfer.isMemberAlegeusGroupTwo}
        alegeusAccountId={hsaBulkTransfer.alegeusHsaAccountId}
      />
    );
  }

  if (
    memberCompletedRequirements &&
    isTodayBeforeFundsTransferDatePlusSevenDays
  ) {
    return (
      <AlegeusLargeAccountsListOldHsaNewHsa217Banner
        isMemberAlegeusGroupTwo={hsaBulkTransfer.isMemberAlegeusGroupTwo}
        alegeusAccountId={hsaBulkTransfer.alegeusHsaAccountId}
      />
    );
  }

  if (
    !memberCompletedRequirements &&
    !isWithinOptInWindow &&
    !isTodayBeforeInvestmentsSellDeadline &&
    isTodayBeforeIndividualTransferReminderExpirationDate
  ) {
    return <AlegeusLargeAccountsListOldHsaNewHsa217V2Banner />;
  }
};

export const displayHsaBulkTransferBannersOnFormaHsaDetailPage = (
  hsaBulkTransfer?: PretaxHsaBulkTransfer
) => {
  if (
    !hsaBulkTransfer ||
    hsaBulkTransfer?.memberStatus === MemberHsaTransferType.Hide
  ) {
    return null;
  }
  const isTodayPriorToOptInWindow = dayjs().isBefore(
    hsaBulkTransfer.optInStartDate
  );

  if (isTodayPriorToOptInWindow) {
    return null;
  }

  const isAlegeusCustodian = hsaBulkTransfer.custodianName === 'alegeus';
  const isNonAlegeusCustodian = !isAlegeusCustodian;
  const memberAcceptedTerms =
    hsaBulkTransfer.memberStatus === MemberHsaTransferType.Accept;

  const isWithinOptInWindow = dayjs().isBetween(
    dayjs(hsaBulkTransfer.optInStartDate),
    dayjs(hsaBulkTransfer.transferDeadline),
    'day',
    '[]'
  );
  const isTodayBeforeFundsTransferDatePlusSevenDays = dayjs().isSameOrBefore(
    dayjs(hsaBulkTransfer.fundsTransferDate).add(7, 'day')
  );

  const isTodayBeforeIndividualTransferReminderExpirationDate =
    dayjs().isSameOrBefore(ALEGEUS_INDIVIDUAL_TRANSFER_REMINDER_LAST_DATE);
  const memberSoldInvestments =
    !hsaBulkTransfer.memberHasAlegeusHsaInvestmentBalance;
  const memberCompletedRequirements =
    memberAcceptedTerms && memberSoldInvestments;

  if (
    isAlegeusCustodian &&
    memberCompletedRequirements &&
    isTodayBeforeFundsTransferDatePlusSevenDays
  ) {
    return (
      <AlegeusLargeAccountsListOldHsaNewHsa217Banner
        isMemberAlegeusGroupTwo={hsaBulkTransfer.isMemberAlegeusGroupTwo}
        alegeusAccountId={hsaBulkTransfer.alegeusHsaAccountId}
      />
    );
  }

  if (
    isAlegeusCustodian &&
    !memberCompletedRequirements &&
    isTodayBeforeIndividualTransferReminderExpirationDate
  ) {
    return <AlegeusLargeAccountsListOldHsaNewHsa217V2Banner />;
  }

  if (isNonAlegeusCustodian && !memberAcceptedTerms && isWithinOptInWindow) {
    return (
      <NonAlegeusLargeNewHsaTransferBanner
        optInDeadline={hsaBulkTransfer.transferDeadline}
        onClick={hsaBulkTransfer.openModal}
      />
    );
  }

  if (
    isNonAlegeusCustodian &&
    memberAcceptedTerms &&
    isTodayBeforeFundsTransferDatePlusSevenDays
  ) {
    return (
      <Banner
        variant={BannerVariant.WARNING}
        content={
          <NonAlegeusSmallStoreConfirmBanner
            fundsTransferDate={hsaBulkTransfer.fundsTransferDate}
          />
        }
      />
    );
  }

  if (
    isNonAlegeusCustodian &&
    !memberAcceptedTerms &&
    !isWithinOptInWindow &&
    isTodayBeforeFundsTransferDatePlusSevenDays
  ) {
    return (
      <Banner
        variant={BannerVariant.WARNING}
        content={<NonAlegeusSmallAccountsListNewHsaMissedBanner />}
      />
    );
  }

  return null;
};

export const displayCalloutOnAlegeusHsaCard = (
  hsaBulkTransfer?: PretaxHsaBulkTransfer
) => {
  if (
    !hsaBulkTransfer ||
    hsaBulkTransfer?.memberStatus === MemberHsaTransferType.Hide
  ) {
    return null;
  }
  const memberAcceptedTerms =
    hsaBulkTransfer.memberStatus === MemberHsaTransferType.Accept;

  const isTodayPriorToOptInWindow = dayjs().isBefore(
    hsaBulkTransfer.optInStartDate
  );

  if (isTodayPriorToOptInWindow) {
    return null;
  }

  const isTodayBeforeInvestmentsSellDeadline = dayjs().isSameOrBefore(
    ALEGEUS_HSA_INVESTMENTS_SELL_DEADLINE
  );
  const isWithinOptInWindow = dayjs().isBetween(
    dayjs(hsaBulkTransfer.optInStartDate),
    dayjs(hsaBulkTransfer.transferDeadline),
    'day',
    '[]'
  );

  const isTodayBeforeGroupOneBlackoutDatePlusSevenDays = dayjs().isSameOrBefore(
    dayjs(ALEGEUS_GROUP_ONE_ACCESS_DATE).add(7, 'day').toDate()
  );
  const isTodayBeforeGroupTwoBlackoutDatePlusSevenDays = dayjs().isSameOrBefore(
    dayjs(ALEGEUS_GROUP_TWO_ACCESS_DATE).add(7, 'day').toDate()
  );
  const shouldShowAlegeusHsaRetailElements = dayjs().isBetween(
    SHOW_ALEGEUS_HSA_RETAIL_ELEMENTS_DATE,
    HIDE_ALEGEUS_HSA_RETAIL_ELEMENTS_DATE,
    'day',
    '[]'
  );
  const memberSoldInvestments =
    !hsaBulkTransfer.memberHasAlegeusHsaInvestmentBalance;
  const memberIsGroupOne = !hsaBulkTransfer.isMemberAlegeusGroupTwo;

  if (
    memberAcceptedTerms &&
    memberSoldInvestments &&
    memberIsGroupOne &&
    !isWithinOptInWindow &&
    !isTodayBeforeInvestmentsSellDeadline &&
    isTodayBeforeGroupOneBlackoutDatePlusSevenDays
  ) {
    return <AlegeusCallout217V1 />;
  }

  if (
    memberAcceptedTerms &&
    memberSoldInvestments &&
    !memberIsGroupOne &&
    !isWithinOptInWindow &&
    !isTodayBeforeInvestmentsSellDeadline &&
    isTodayBeforeGroupTwoBlackoutDatePlusSevenDays
  ) {
    return <AlegeusCallout217V1 />;
  }

  if (
    hsaBulkTransfer.memberHasAlegeusRetailHsa &&
    !memberAcceptedTerms &&
    shouldShowAlegeusHsaRetailElements
  ) {
    return <AlegeusCalloutRetail />;
  }

  return null;
};
